import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@customerio/pluma-components/react';
import {
  ClearConditionsLink,
  ConditionGroup,
  OBJECT_ATTRIBUTE_TYPE,
  OBJECT_FILTER_FORMAT,
  OBJECT_FILTER_OPERATOR_FAMILIES,
  ObjectAttributeRow,
  normalizeObjectFilterWire,
  objectFilterBuilder,
  parseObjectFilterWire,
  type ObjectAttributeFilterState,
  type ObjectFilterWire,
  type TypeOptionGroup,
} from '@/features/dryad';
import { safeQueryDecode } from '@/utils/condition-query-encoding';

type FilterAttributeRowProps = { id: string };

/**
 * The shared object-attribute row with this surface's arguments: the operators
 * an object filter offers, and the attribute names the page already holds.
 */
function FilterAttributeRow({ id }: FilterAttributeRowProps) {
  const meta = objectFilterBuilder.useMeta();

  return (
    <ObjectAttributeRow
      id={id}
      conditionType={OBJECT_ATTRIBUTE_TYPE}
      families={OBJECT_FILTER_OPERATOR_FAMILIES}
      localAttributeNames={meta.attributeNames}
    />
  );
}

objectFilterBuilder.registerView(OBJECT_ATTRIBUTE_TYPE, FilterAttributeRow);

const EMPTY_CONDITIONS = { and: [] };
const TYPE_OPTIONS: TypeOptionGroup[] = [
  { label: 'Object', options: [{ value: OBJECT_ATTRIBUTE_TYPE, label: 'Attribute' }] },
];

export type ObjectFilterBuilderProps = {
  filters: string | null;
  objectTypeId: string;
  attributeNames: readonly string[];
  onChange: (serialized: ObjectFilterWire, isValid: boolean, isEmpty: boolean) => void;
};

export function ObjectFilterBuilder({ filters, objectTypeId, attributeNames, onChange }: ObjectFilterBuilderProps) {
  const initialConditions = useMemo(
    () => normalizeObjectFilterWire(filters == null ? EMPTY_CONDITIONS : safeQueryDecode(filters), objectTypeId),
    [filters, objectTypeId],
  );
  const numericTypeId = Number(objectTypeId);
  const seededTypeId = Number.isNaN(numericTypeId) ? objectTypeId : numericTypeId;
  const builder = objectFilterBuilder.useConditionBuilder({
    initialValue: initialConditions,
    format: OBJECT_FILTER_FORMAT,
    seeds: {
      [OBJECT_ATTRIBUTE_TYPE]: (): Partial<ObjectAttributeFilterState> => ({ typeId: seededTypeId }),
    },
  });
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  useEffect(() => {
    const output = builder.sources.output(OBJECT_FILTER_FORMAT);
    return output.subscribe(() => {
      const { serialized, isValid, isEmpty } = output.get();
      const wire = parseObjectFilterWire(serialized);
      if (wire != null) onChangeRef.current(wire, isValid, isEmpty);
    });
  }, [builder]);

  return (
    <Box data-testid="object-filter-builder">
      <objectFilterBuilder.DryadProvider
        builder={builder}
        meta={{ isDisabled: false, typeOptions: TYPE_OPTIONS, attributeNames }}
      >
        <ConditionGroup
          conditionTypeOf={(value) => (value === OBJECT_ATTRIBUTE_TYPE ? OBJECT_ATTRIBUTE_TYPE : null)}
          headerActions={<ClearConditionsLink />}
        />
      </objectFilterBuilder.DryadProvider>
    </Box>
  );
}
